import { useEffect, useRef, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import type { Photo } from '../types';

interface SSEMessage {
  type: 'connected' | 'new_photos';
  photos?: Photo[];
}

export function usePhotoStream() {
  const { addPhoto, viewMode } = useApp();
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);

  const connect = useCallback(() => {
    // Only connect when in collage or gallery mode
    if (viewMode === 'countdown') {
      return;
    }

    // Clean up existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const eventSource = new EventSource('/api/photos/stream');
    eventSourceRef.current = eventSource;

    eventSource.onmessage = (event) => {
      try {
        const data: SSEMessage = JSON.parse(event.data);
        
        if (data.type === 'new_photos' && data.photos) {
          data.photos.forEach(photo => {
            addPhoto(photo);
          });
        }
      } catch (error) {
        console.error('Error parsing SSE message:', error);
      }
    };

    eventSource.onerror = () => {
      eventSource.close();
      eventSourceRef.current = null;
      
      // Reconnect after 5 seconds
      reconnectTimeoutRef.current = window.setTimeout(() => {
        connect();
      }, 5000);
    };

    eventSource.onopen = () => {
      console.log('SSE connection established');
    };
  }, [addPhoto, viewMode]);

  useEffect(() => {
    connect();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [connect]);

  return { reconnect: connect };
}

